import { z } from 'zod';

/**
 * BYOK vault DTO — request/response shapes for /api/teams/:slug/ai/byok-keys.
 *
 * Phase 1 follow-up #2.  Plaintext key never round-trips through the API
 * after the initial store — list responses ship the last-4-chars hint
 * only (`keyHint`).  See docs/ai-platform/11-MULTI-PROVIDER-STATUS.md.
 */

export const ByokProvider = z.enum(['anthropic', 'openai', 'openrouter']);
export type ByokProviderValue = z.infer<typeof ByokProvider>;

export const StoreByokKeySchema = z.object({
  provider: ByokProvider,
  /** Raw key as provided by the customer (e.g. "sk-ant-..."). Never logged. */
  apiKey: z.string().min(8).max(1024),
  /** When true, store as team-shared (any member can call AI with this key).
   *  When false, store as a personal override for the calling user. */
  teamShared: z.boolean().default(false),
  /** Optional human label ("OpenAI prod 2026" etc.). */
  label: z.string().min(1).max(80).nullable().optional(),
});
export type StoreByokKeyInput = z.infer<typeof StoreByokKeySchema>;

export const ByokKeyMetadataSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  /** null = team-shared row. */
  userId: z.string().uuid().nullable(),
  provider: ByokProvider,
  /** Last 4 plaintext chars (e.g. "AbCd") — for `sk-...AbCd` UI hint. */
  keyHint: z.string().nullable(),
  label: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type ByokKeyMetadataProjection = z.infer<typeof ByokKeyMetadataSchema>;

/** Result of the cheap provider check run when a key is saved:
 *  valid = provider accepted it; unverified = provider unreachable / rate
 *  limited, so the key was saved without proof.  An `invalid` key is
 *  refused with 422 `byok_key_invalid` and never stored. */
export const ByokKeyValidationStatus = z.enum(['valid', 'invalid', 'unverified']);
export type ByokKeyValidationStatus = z.infer<typeof ByokKeyValidationStatus>;

/** POST /byok-keys response: the stored metadata plus the save-time check. */
export const StoreByokKeyResponseSchema = ByokKeyMetadataSchema.extend({
  validation: ByokKeyValidationStatus,
});
export type StoreByokKeyResponse = z.infer<typeof StoreByokKeyResponseSchema>;

export const ByokKeyListResponseSchema = z.object({
  items: z.array(ByokKeyMetadataSchema),
  /** Mirror of the AI Bridge resolver precedence for the UI.  Per provider:
   *  whether a key is available for THIS user when they call AI (resolves
   *  user-scoped → team-scoped → null).  Saves the client from re-implementing
   *  precedence to render the "configured / not configured" pill. */
  resolved: z.object({
    anthropic: z.boolean(),
    openai: z.boolean(),
    openrouter: z.boolean(),
  }),
});
export type ByokKeyListResponse = z.infer<typeof ByokKeyListResponseSchema>;
